import dayjs from "dayjs";
import { OrderJson, TransactionType } from "@smeetz/orders";
import { Channel, DiscountType, OrderSession } from "@smeetz/order-common";
import type {
  CompanyInfo,
  FiscalInformation,
  Locale,
  ReceiptCountry,
  ReceiptInput,
} from "@smeetz/receipt";
import { IBookingReceipt, IReceiptGroupInfo } from "@/interfaces/receipts";
import DeviceModule from "@/store/modules/device";
import UserModule from "@/store/modules/user";
import CustomersModule from "@/store/modules/customers";
import POSConfigModule from "@/store/modules/pos-config";
import i18n from "@/lang";

/** Countries for which the POS holds per-sale fiscal signature info. */
const FISCAL_COUNTRIES: ReceiptCountry[] = ["FR", "AT", "DE", "IT", "ES", "HR"];

/** Map the active POS i18n locale → package Locale (default 'en'). */
function mapLocale(): Locale {
  const raw = (i18n.global as any)?.locale;
  const l = String(raw?.value ?? raw ?? "en").toLowerCase();
  const supported: Locale[] = ["en", "fr", "de", "it", "es", "hr", "nl"];
  return (supported as string[]).includes(l) ? (l as Locale) : "en";
}

export function buildOrganizer(
  g: IReceiptGroupInfo,
  country: string,
  receiptInfo?: IBookingReceipt
): CompanyInfo {
  // Header name (legacy HeaderNamePartial rule): DE → registered company name,
  // ES → public display name, else trading name.
  const headerName = receiptInfo?.germanyAdditionalInfo
    ? g.companyName || g.name
    : receiptInfo?.spainAdditionalInfo
    ? g.displayName || g.name
    : g.name;
  return {
    name: headerName || "",
    address: g.address || "",
    city: g.city,
    zip: g.zip,
    country,
    phone: g.phone,
    vatNumber: g.vatNumber,
    registrationNum: g.registrationNum,
    exoNumber: g.exoNumber,
    // France legal identifiers
    siret: g.siret,
    siren: g.siren,
    naf: g.nafCode,
    rcs: g.rcs,
    socialCapital: g.socialCapital,
    legalForm: g.legalForm,
    // Spain CIF
    nif: g.cif || g.vatId,
  };
}

/**
 * The session the receipt documents: the current one when it carries lines,
 * else the newest previous session that does (same rule as buildBaseReceiptInfo).
 */
export function receiptSessionOf(order: OrderJson): OrderJson["orderSession"] {
  if (order.orderSession.orderLines.length > 0) {
    return order.orderSession;
  }
  return (
    [...order.previousSessions]
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )
      .find((session) => session.orderLines.length > 0) ?? order.orderSession
  );
}

/**
 * Document date = the EFR-signed sale time when France stashed one (verbatim,
 * tamper-resistant — see FIS-652/L3-8341), else the latest payment createdAt,
 * else the order creation. LOCAL wall-clock ISO (no `Z`): the package prints
 * ISO fields verbatim, a UTC instant would print the till time an hour off.
 */
function resolveDocumentDate(
  session: OrderJson["orderSession"],
  order: OrderJson,
  receiptInfo: IBookingReceipt
): string {
  const efrTs = receiptInfo.franceAdditionalInfo?.efrSaleTimestamp;
  if (efrTs && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(efrTs)) {
    return efrTs.replace(" ", "T");
  }
  let latest: string | undefined;
  for (const payment of session.payments) {
    if (payment.type !== TransactionType.PAYMENT || !payment.createdAt)
      continue;
    if (!latest || dayjs(payment.createdAt).isAfter(dayjs(latest)))
      latest = String(payment.createdAt);
  }
  return dayjs(latest || order.createdAt).format("YYYY-MM-DDTHH:mm:ss");
}

/**
 * Assert the identifiers a country's law mandates on a SIGNED receipt are
 * present; a gap would render as a blank row that looks fiscal but isn't.
 * Throwing makes printBookingReceipt fall back to the legacy template.
 */
function requireFiscalFields(
  context: string,
  fields: Record<string, string | undefined>
): void {
  const missing = Object.keys(fields).filter((k) => !fields[k]);
  if (missing.length) {
    throw new Error(
      `Receipt: ${context} fiscal data incomplete — missing ${missing.join(
        ", "
      )}`
    );
  }
}

/**
 * Per-sale fiscal blob from the already-computed `receiptInfo.*AdditionalInfo`.
 * NOL scope: France (compliance-common-v2). The other fiscal countries still
 * print via the legacy template on the order flow (their signing is not
 * migrated yet), so no fiscal blob is built for them here.
 */
export function buildFiscalInformation(
  receiptInfo: IBookingReceipt,
  country: string,
  documentDate: string
): { fiscal?: FiscalInformation; isReprint: boolean } {
  if (
    !(FISCAL_COUNTRIES as string[]).includes(country) ||
    !receiptInfo.isSignatureRequired
  ) {
    return { fiscal: undefined, isReprint: false };
  }

  if (country !== "FR" || !receiptInfo.franceAdditionalInfo) {
    return { fiscal: undefined, isReprint: false };
  }

  const f = receiptInfo.franceAdditionalInfo;
  const isReprint = !!f.isReprint;
  // `documentTypexOperation` is "${documentType} / ${operationType}"; the package
  // rebuilds that string itself, so split it back to the two atomic parts.
  const [documentType, ...operationParts] = (
    f.documentTypexOperation || ""
  ).split(" / ");
  const operationType = operationParts.join(" / ") || undefined;

  const sec = f.signature ?? "";
  const swVersion = f.swVersion ?? "";
  const printCount = f.printCount ?? "";
  const fiscalNumber = f.fiscalNumber ?? "";
  const originalFiscalNumber = f.originalFiscalNumber ?? "";
  const reprintTimestamp = f.reprintTimestamp ?? "";
  if (receiptInfo.isSigned) {
    // NF525 mandates these on every signed FR receipt; a reprint additionally
    // mandates the original fiscal number + reprint timestamp.
    requireFiscalFields("FR", { sec, swVersion, printCount, fiscalNumber });
    if (isReprint) {
      requireFiscalFields("FR reprint", {
        originalFiscalNumber,
        reprintTimestamp,
      });
    }
  }

  const fiscal: FiscalInformation = {
    country: "FR",
    fiscalNumber,
    signature: sec, // FR's signing chain IS the sec code
    signedAt: documentDate,
    deviceUid: f.posId || DeviceModule.hardwareLocation?.deviceUid || "",
    countrySpecific: {
      france: {
        sec,
        swVersion,
        printCount,
        documentType,
        operationType,
        // Reprint-only data; the timestamps are the EFR's own strings, printed
        // verbatim by the package (no timezone math — FIS-652/L3-8341).
        reprint: isReprint
          ? {
              originalFiscalNumber,
              reprintTimestamp,
              originalSignature: f.originalSignature,
              originalPrintTimestamp: `${receiptInfo.date} ${receiptInfo.hour}`,
              swVersionOriginal: f.swVersionOriginal,
              originalSellerName: f.originalSellerName,
            }
          : undefined,
      },
    },
  };
  return { fiscal, isReprint };
}

/**
 * Assemble the @smeetz/receipt ReceiptInput for an ORDER (new order logic).
 * The v2 order session is already the canonical order-common shape the package
 * consumes (payments in minor units, vatRate fractions, discounts with VAT,
 * vouchers as tender) — no reconstruction or unit normalization needed.
 */
export function buildOrderReceiptInput(
  order: OrderJson,
  receiptInfo: IBookingReceipt
): ReceiptInput {
  const session = receiptSessionOf(order);
  // ISO alpha-2 from the group config — receiptInfo.groupInfo.country is a
  // display NAME ("France"), not a code, and the package keys its
  // country-conditional rendering (VAT letters, legal rows) on the code.
  const country = (POSConfigModule.getGroupCountryCode || "").toUpperCase();
  const documentDate = resolveDocumentDate(session, order, receiptInfo);
  const { fiscal, isReprint } = receiptInfo.isBill
    ? { fiscal: undefined, isReprint: false }
    : buildFiscalInformation(receiptInfo, country, documentDate);

  // Reactive store session — clone before any display-only reshaping.
  const sessionCopy: OrderSession = JSON.parse(JSON.stringify(session));

  // The ledger (`discounts[]`) is the only discount truth here; drop the
  // order-level config so the package can't allocate phantom notes from it.
  (sessionCopy as { orderDiscounts?: unknown[] }).orderDiscounts = [];

  // Line VAT is already discount-netted on the v2 session, but the package
  // nets again from discount vatValue — strip it so line VAT prints as-is,
  // and fold the ledger into line totals ourselves. Feed discount magnitudes:
  // the package template hardcodes the minus (a negative row printed "--10").
  const discountByLine = new Map<string, number>();
  for (const discount of sessionCopy.discounts ?? []) {
    if (discount.type === DiscountType.VOUCHER) continue;
    discountByLine.set(
      discount.lineId,
      (discountByLine.get(discount.lineId) ?? 0) + discount.value
    );
    delete (discount as { vatValue?: unknown }).vatValue;
    delete (discount as { vatRate?: unknown }).vatRate;
    (discount as { value: number }).value = Math.abs(discount.value);
  }
  // A refund's session discount is negative; fold it into sales (total
  // unchanged) and zero it, hiding the summary row like legacy's `> 0` gate.
  const sessionDiscount = sessionCopy.discount ?? 0;
  if (sessionDiscount < 0) {
    (sessionCopy as { sales: number }).sales =
      sessionCopy.sales - sessionDiscount;
    (sessionCopy as { discount?: number }).discount = 0;
  }

  // A refund's ledger row points at the original sale line (absent from the
  // refund session); the package keys notes strictly by lineId, so re-key it
  // to the cancel line (its discountsFromLedger has no mainLineId fallback).
  const lineIds = new Set(sessionCopy.orderLines.map((line) => line.id));
  for (const discount of sessionCopy.discounts ?? []) {
    if (discount.type === DiscountType.VOUCHER) continue;
    if (lineIds.has(discount.lineId)) continue;
    const cancelLine = sessionCopy.orderLines.find(
      (line) => line.quantity < 0 && line.mainLineId === discount.lineId
    );
    if (cancelLine) {
      (discount as { lineId: string }).lineId = cancelLine.id;
    }
  }
  for (const line of sessionCopy.orderLines) {
    const own = discountByLine.get(line.id);
    const fallback =
      line.quantity < 0 && line.mainLineId
        ? discountByLine.get(line.mainLineId)
        : undefined;
    const lineDiscount = own ?? fallback ?? 0;
    if (lineDiscount) {
      line.totalPrice = line.totalPrice - lineDiscount;
    }
  }

  // Cash tender + change (legacy same-currency rule): show the bill tendered
  // and synthesize the give-back CHANGE row. Exact tender drops the redundant
  // detail; cross-currency keeps multiCurrencyInfo for the package's detail.
  const withChange: typeof sessionCopy.payments = [];
  for (const payment of sessionCopy.payments) {
    withChange.push(payment);
    const mci = payment.multiCurrencyInfo;
    if (
      !mci ||
      payment.type !== TransactionType.PAYMENT ||
      !mci.providedCashBill ||
      (mci.paymentCurrency && mci.paymentCurrency !== sessionCopy.currency) ||
      (mci.exchangeRate ?? 1) !== 1
    ) {
      continue;
    }
    const changeMinor = mci.providedCashBill - payment.value;
    delete (payment as { multiCurrencyInfo?: unknown }).multiCurrencyInfo;
    if (changeMinor > 0) {
      (payment as { value: number }).value = mci.providedCashBill;
      withChange.push({
        ...payment,
        id: `${payment.id}-change`,
        paymentMethod: "CHANGE" as typeof payment.paymentMethod,
        value: -changeMinor,
        multiCurrencyInfo: undefined,
        adyenPaymentInfo: undefined,
      });
    }
  }
  (sessionCopy as { payments: typeof withChange }).payments = withChange;
  // The package's document date is the max payment timestamp; stamp them all
  // with the resolved date so a raw Z-tagged row can't shift the printed hour.
  for (const payment of sessionCopy.payments) {
    (payment as { createdAt?: unknown }).createdAt = documentDate;
    (payment as { completedAt?: unknown }).completedAt = documentDate;
  }

  const { firstName, lastName } = UserModule.user;
  const customerId = CustomersModule.getSelectedCustomerId;

  // Legacy leads with the ORDER ref ("Ref"); the package's "Reference" reads
  // orderSession.reference (the session ref). Point it at the order ref — the
  // session ref stays in meta.receiptNumber (the package's second row).
  if (order.reference) {
    (sessionCopy as { reference?: string }).reference = order.reference;
  }

  return {
    organizer: buildOrganizer(receiptInfo.groupInfo, country, receiptInfo),
    orderSession: sessionCopy,
    fiscal,
    meta: {
      locale: mapLocale(),
      documentDate,
      operator: receiptInfo.franceAdditionalInfo?.sellerName
        ? { name: receiptInfo.franceAdditionalInfo.sellerName }
        : { firstName, lastName },
      customer: customerId
        ? {
            id: String(customerId),
            firstName: CustomersModule.getSelectedCustomerFirstName,
            lastName: CustomersModule.getSelectedCustomerLastName,
          }
        : undefined,
      channel: Channel.SMEETZ_POS,
      receiptNumber: receiptInfo.orderSessionReference || undefined,
      isReprint: isReprint || !!receiptInfo.isReceiptCopy || undefined,
      isBill: receiptInfo.isBill || undefined,
    },
  };
}
